import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PressableScale } from "pressto";
import { useFocusEffect } from "@react-navigation/native";
import { Stack } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { AlarmSettings } from "@src/features/alarms/screens/AlarmSettings";
import { useCameras } from "@src/features/cameras";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { DataStateView, EmptyStateProps } from "@src/components/screen-state";
import { getCameraKeyString, isLegacyAppCamera } from "@src/utils/key-parser";
import { CameraFilterBottomSheet } from "@src/features/cameras/components/CameraFilterBottomSheet";
import { useTenantId } from "@src/features/tenants";

export function LegacyAlarmsScreen() {
  const { data: cameras, refetch: refetchCameras, isLoading, isFetching, error } = useCameras();
  const { theme } = useUnistyles();
  const tenantId = useTenantId();
  const [selectedCameraKey, setSelectedCameraKey] = useState<string>("");
  const bottomSheetRef = useRef<BottomSheetModal | null>(null);

  const prevTenantRef = useRef(tenantId);
  useEffect(() => {
    if (prevTenantRef.current !== tenantId) {
      prevTenantRef.current = tenantId;
      setSelectedCameraKey("");
    }
  }, [tenantId]);

  useFocusEffect(
    useCallback(() => {
      refetchCameras();
    }, [refetchCameras]),
  );

  const cameraAssetPart = useMemo(() => {
    if (!selectedCameraKey) return "Alarms";
    const parts = selectedCameraKey.split("@");
    return parts.length > 0 ? parts[0] : selectedCameraKey;
  }, [selectedCameraKey]);

  const cameraIdPart = useMemo(() => {
    if (!selectedCameraKey) return "";
    const parts = selectedCameraKey.split("@");
    return parts.length > 1 ? parts[1] : "";
  }, [selectedCameraKey]);

  const supportedCameras = useMemo(() => {
    if (!cameras) return [];
    return cameras.filter((camera) => !isLegacyAppCamera(camera));
  }, [cameras]);

  const hasCamerasButOnlyLegacy = useMemo(() => {
    return cameras && cameras.length > 0 && supportedCameras.length === 0;
  }, [cameras, supportedCameras]);

  useEffect(() => {
    if (supportedCameras && supportedCameras.length > 0 && !selectedCameraKey) {
      const firstCameraKey = getCameraKeyString(supportedCameras[0]);
      if (firstCameraKey) {
        setSelectedCameraKey(firstCameraKey);
      }
    }
  }, [supportedCameras, selectedCameraKey]);

  const selectedCamera = useMemo(() => {
    if (!selectedCameraKey || !supportedCameras) return null;
    return supportedCameras.find((camera) => {
      const cameraKey = getCameraKeyString(camera);
      return cameraKey === selectedCameraKey;
    });
  }, [selectedCameraKey, supportedCameras]);

  const selectedSiteId = useMemo(() => {
    return selectedCamera?.site?.id;
  }, [selectedCamera]);

  const headerTitle = useCallback(
    () => (
      <View flexDirection="row" alignItems="center" gap="xs">
        <Text textStyle="md/medium">{cameraAssetPart}</Text>
        {cameraIdPart && (
          <Text textStyle="md/medium" color="textMuted">
            {cameraIdPart}
          </Text>
        )}
      </View>
    ),
    [cameraAssetPart, cameraIdPart],
  );

  const handleSelectCamera = (cameraKey: string | null) => {
    if (!cameraKey) return;
    setSelectedCameraKey(cameraKey);
    bottomSheetRef.current?.close();
  };

  const headerRight = useCallback(
    () => (
      <PressableScale
        onPress={() => {
          bottomSheetRef.current?.present();
        }}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        style={{
          paddingHorizontal: 16,
          paddingVertical: 4,
        }}
      >
        <Icon name="settingsOutline" size={24} color={theme.colors.icon} />
      </PressableScale>
    ),
    [theme.colors.icon],
  );

  const headerOptions = useMemo(() => {
    const baseOptions = {
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
      headerTintColor: theme.colors.text,
      headerBackButtonDisplayMode: "minimal" as const,
    };

    if (isLoading || !supportedCameras || supportedCameras.length === 0) {
      return {
        ...baseOptions,
        title: "Alarms",
      };
    }

    return {
      ...baseOptions,
      headerTitle,
      headerRight,
      headerShadowVisible: false,
    };
  }, [theme.colors.background, theme.colors.text, isLoading, supportedCameras, headerTitle, headerRight]);

  const emptyState = useMemo((): EmptyStateProps => {
    if (hasCamerasButOnlyLegacy) {
      return {
        icon: "videoOff",
        title: "Upgrade device fleet",
        message:
          "The new and improved alarm schedule management system, including the AI Schedule Assistant, is only available for the new fleet of devices. Get in touch with your account manager to discuss upgrading your device fleet.",
      };
    }
    return {
      icon: "videoOff",
      title: "No cameras found",
      message: "There are no cameras available to configure alarm settings.",
    };
  }, [hasCamerasButOnlyLegacy]);

  return (
    <>
      <Stack.Screen options={headerOptions} />
      <DataStateView
        data={cameras}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetchCameras}
        isEmpty={(allCameras) => {
          if (!allCameras) {
            return false;
          }
          const supported = allCameras.filter((camera) => !isLegacyAppCamera(camera));
          return supported.length === 0;
        }}
        loadingMessage="Loading alarms..."
        errorTitle="Unable to load alarms"
        emptyState={emptyState}
        showStaleDataBanner={false}
      >
        {() => (
          <>
            <AlarmSettings cameraKey={selectedCameraKey} siteId={selectedSiteId} showCameraSelection={true} />
            {supportedCameras && (
              <CameraFilterBottomSheet
                bottomSheetRef={bottomSheetRef}
                cameras={supportedCameras}
                selectedCameraKey={selectedCameraKey}
                onSelectCamera={handleSelectCamera}
                title="Select Camera"
                subtitle="Choose a camera to view its alarm settings"
              />
            )}
          </>
        )}
      </DataStateView>
    </>
  );
}
